/**
 * What is still missing before the Flugdurchführungsplan is a finished document.
 *
 * The PDF is produced from an EMPTY form just as happily as from a full one —
 * an empty form is a legal blank and there is no reason to refuse it. What the
 * card does instead is say, before the download rather than after it, which of
 * the cells a school or a briefing officer will look for first are still open.
 *
 * The list is deliberately short and deliberately not "every required field":
 * the LBA form has some four hundred cells, most of them situational (an
 * alternate, a second ATIS row, a mass-and-balance column for a third seat), and
 * a card that counted all of them would be a card that always says "87 missing".
 * These eight are the ones that are missing from a plan that is genuinely
 * unfinished, and present in every plan that is not:
 *
 *   · **when** — `datum`, `etd`
 *   · **where** — `von`, `nach`, and `total_dist`, which is empty exactly when no
 *     route has been drawn at all
 *   · **what with** — `lfz_muster`, `lfz_kennz`
 *   · **how far it gets** — `fuel_vorrat_l`
 *
 * Pure, and the ids are exported so the test names the same eight the card does.
 */

/** The cells the card counts, in the order it lists them. */
export const REQUIRED_IDS: readonly string[] = [
  'datum',
  'etd',
  'von',
  'nach',
  'total_dist',
  'lfz_muster',
  'lfz_kennz',
  'fuel_vorrat_l',
];

/** How many of them the card names before it starts counting the rest. */
export const NAMED_LIMIT = 3;

/**
 * Which of them are still empty, in the order above.
 *
 * A cell holding only spaces is empty: the PDF would stamp nothing there, so
 * calling it filled would be a lie the pilot only finds out about on paper.
 */
export function missingIds(
  read: (id: string) => string,
  ids: readonly string[] = REQUIRED_IDS,
): string[] {
  return ids.filter(id => String(read(id) ?? '').trim() === '');
}

/**
 * The field's name, trimmed to what a card can print.
 *
 * `fields.label()` produces the FORM's own label, which is written for someone
 * looking at the paper („ETD – voraussichtliche Startzeit (UTC)"): exactly right
 * beside the cell, three times too long in a comma-separated list of three. The
 * head of it — everything before the dash or the bracket — is the name; the rest
 * is the explanation, and the explanation is already on the form.
 */
export function shortLabel(text: string): string {
  return text.split(/\s[–—-]\s|\s\(/)[0]?.trim() || text;
}

/** The count alone — what the card's badge shows. */
export const missingCount = (
  read: (id: string) => string,
  ids: readonly string[] = REQUIRED_IDS,
): number => missingIds(read, ids).length;
